import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MVX_TRAINING } from '../../models/MetabolicVulnerability';

/**
 * MVX "Training" tab — Dr. Darren Schmidt's MVX Plus program.
 *
 * PRACTITIONER-ONLY, and the reason it is a tab rather than a block in the
 * Insights panel. The content is not per-client: unlike everything else in
 * this section it is byte-identical on every test, for every client, forever.
 * It also sat directly beneath "Observational — not diagnostic", so a clinical
 * disclaimer ran straight into a paid-course pitch. A tab gives it a home that
 * is opt-in, has room for the three points, and keeps the clinical column
 * clinical.
 *
 * THE OFFER ON IT IS HIS, not ours. Dr. Schmidt gives practitioners who come
 * through this tab a discount on his own program (2026-10-13). There is still
 * no commission, affiliate arrangement or money of any kind between the two
 * companies, and the tab must not suggest otherwise.
 *
 * It is a TAB, so it is screen chrome and the client's PDF never renders it —
 * no flag to pass and none to forget.
 *
 * Phase 1 of the collaboration ships this INSTEAD of his 7-Step Blueprint
 * panel (see mvxBlueprintVisible): rather than reproducing his framework for
 * free, point practitioners at the place he teaches it.
 */

/**
 * EVERY WORD ON THE TAB, in one place.
 *
 * Exported because it is a partner's page as much as ours: Dr. Schmidt gets it
 * as a CSV to review and edit (scripts/generateMvxTrainingTab.ts), and a copy
 * deck he is editing has to be the copy the tab renders, not a transcription
 * of it. The rest of the words on the tab come from MVX_TRAINING, which the
 * attribution line and the PDF share.
 *
 * `covers` is the program in his own framing: interpret, apply, track.
 *
 * The provenance in `intro` and the argument in `ranges` are HIS OWN ACCOUNT,
 * given on the 2026-10-19 training-material review call and cleared by him for
 * print the next day (Maryann, 2026-10-20): the 2018 date on the Blueprint, the
 * January 2025 Labcorp release, that he is the one who got Labcorp to release
 * the test, and that he trains Labcorp's MVX researchers. We have not verified
 * any of it independently. It prints on his say-so, which is the same footing
 * as `MVX_TRAINING.credential`.
 *
 * NOT here, deliberately: his claim that new studies track MVX Plus alongside
 * thyroid and lipid panels for longevity and catch diabetes patterns that
 * glucose and A1c miss. He named no studies, so there is nothing to cite and it
 * stays off the tab until we have the papers (Maryann, 2026-10-20).
 */
export const TRAINING_COPY = {
  tab: 'Training',
  heading: 'Train with Dr. Darren Schmidt',
  intro:
    'The optimal ranges on every bar in this section are his, not Labcorp’s. Dr. Schmidt ' +
    'developed the 7-Step Blueprint to Optimal Health in 2018, and he is the reason MVX Plus is ' +
    'on the Labcorp menu: he is the one who got Labcorp to release the test, and he has run it ' +
    'with patients since its January 2025 release. He develops the protocols it is read against, ' +
    'and he trains Labcorp’s MVX researchers. His program is where the method behind these ' +
    'targets is taught.',
  // Why a practitioner should not read a lab-normal result as a finished answer,
  // in his framing: a reference range is built to hold most of the population,
  // so it describes what is common rather than what is optimal. The 75% figure
  // and "Labcorp will not narrow them" are both his (2026-10-19).
  ranges:
    'A reference range is built to hold roughly 75% of the population inside normal, so ' +
    'Labcorp’s ranges describe what is common rather than what is optimal, and Labcorp will ' +
    'not narrow them. The optimal and functional bands on these bars are the targets Dr. Schmidt ' +
    'treats toward, which is why a result can sit inside the lab’s range and still be worth ' +
    'acting on.',
  covers: [
    ['Interpret', 'Read a result the way he does — which marker to start with, and why the lab-normal ones still matter.'],
    ['Apply', 'Turn the reading into a protocol in the room, rather than after the fact.'],
    // His own framing of why a client comes back for a second draw: it shows the
    // protocol is working, and it shows whether they stayed with it (2026-10-19).
    ['Track', 'Use repeat draws to show a client the protocol is working, and whether they are staying with it.'],
  ],
  // Runs on from `credential` as one paragraph in small grey type.
  disclaimer:
    'Dr. Schmidt is an independent practitioner and educator; his program is his own and is ' +
    'not part of a LabSmarts subscription.',
};

const COVERS = TRAINING_COPY.covers;

export class MvxTrainingSection extends React.Component {
  render() {
    return (
      <div style={{ maxWidth: 680 }}>
        <span className="consideration-title">{TRAINING_COPY.heading}</span>
        <p style={{ marginTop: 8, marginBottom: 12, lineHeight: 1.6 }}>{TRAINING_COPY.intro}</p>
        <p style={{ marginTop: 0, marginBottom: 14, lineHeight: 1.6 }}>{TRAINING_COPY.ranges}</p>

        <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
          {COVERS.map(([title, body]) => (
            <li key={title} style={{ display: 'flex', gap: 10, marginBottom: 9, lineHeight: 1.5 }}>
              <span style={{ flex: '0 0 72px', fontWeight: 700, color: '#2a2a2a' }}>{title}</span>
              <span style={{ color: '#5c564f' }}>{body}</span>
            </li>
          ))}
        </ul>

        <a
          href={MVX_TRAINING.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            background: '#337ab7',
            border: '1px solid #337ab7',
            borderRadius: 4,
            padding: '9px 18px',
            fontSize: 14,
            fontWeight: 600,
            color: '#fff',
            textDecoration: 'none',
          }}
        >
          {MVX_TRAINING.label}&nbsp;
          <FontAwesomeIcon icon="external-link-alt" style={{ fontSize: 11 }} />
        </a>

        {MVX_TRAINING.promoCode ? (
          <div style={{ marginTop: 12, fontSize: 13, color: '#5c564f' }}>
            Use code{' '}
            <strong style={{ color: '#2a2a2a', letterSpacing: '0.04em' }}>{MVX_TRAINING.promoCode}</strong>
            {MVX_TRAINING.promoNote ? <span style={{ color: '#857d73' }}> for {MVX_TRAINING.promoNote}</span> : null}
          </div>
        ) : null}

        <div style={{ marginTop: 16, fontSize: 11.5, color: '#a49c92', lineHeight: 1.6 }}>
          {MVX_TRAINING.credential} {TRAINING_COPY.disclaimer}
        </div>
      </div>
    );
  }
}

export default MvxTrainingSection;
